import type {
  AccessRole,
  ArrangementStatusWord,
  DashboardShortcutTarget,
  DashboardTileId,
  TileSize,
  FillState,
  FillWord,
  ArrangementStatus,
  DeploymentStatus,
  OrgUnitElevation,
  OrgUnitSubType,
  OrgUnitType,
  PolicyVersionStatus,
  LevelledAccessRole,
  NodalLevel,
  Presence,
  RaiserLevel,
  TemplateDecisionClass,
  TemplateRunOutcome,
} from '@pams/shared';
import {
  ARRANGEMENT_TYPES,
  arrangementStatusWordOf,
  fillWordOf,
  isArrangementType,
  isUnanswered,
  nodalLevelOf,
} from '@pams/shared';
import type { StatusTone } from '@/theme/statusTone';

/**
 * Display labels for the shared enums.
 *
 * Written out rather than derived with `.toLowerCase()` + a `capitalize` class, which
 * turns `PS` into "Ps" and `A-ASI` into "A-asi". Acronyms are most of this domain's
 * vocabulary, so a rule that mangles them is the wrong rule.
 *
 * They live on the web side because they are presentation: the API's enum values are the
 * contract, and a label change must never be able to alter one.
 */
export const ORG_UNIT_TYPE_LABEL: Record<OrgUnitType, string> = {
  COMMISSIONERATE: 'Commissionerate',
  DIVISION: 'Division',
  UNIT: 'Unit',
};

/** [C5c §11h] What a unit's roles act at — the words the users page reads under the permissions field. */
export const ORG_UNIT_ELEVATION_LABEL: Record<OrgUnitElevation, string> = {
  NONE: 'Its own level',
  ONE_UP: 'One level up',
  TWO_UP: 'Two levels up',
};

export const ORG_UNIT_SUBTYPE_LABEL: Record<OrgUnitSubType, string> = {
  PS: 'Police station',
  /**
   * [amended INGEST] The client's own rendering is the suffix — `Tollygunge(W)` — and
   * the label spells it out, because a badge reading "(W)" beside a name already ending
   * in "(W)" says nothing twice.
   */
  WOMEN_PS: "Women's police station",
  TRAFFIC_GUARD: 'Traffic guard',
  RESERVE_FORCE: 'Reserve force',
  BATTALION: 'Battalion',
  /**
   * [amended WINGS] A commissionerate-level DIVISION that is not territorial — the
   * Detective Department, Special Branch, the armed battalions. The badge says "Wing"
   * rather than "Division" so an operator can tell the twenty-six apart from the ten at
   * a glance; `orgUnitKindLabel` below returns the TYPE label only for `OTHER`, which is
   * exactly why the placeholder could not carry this meaning.
   */
  WING: 'Wing',
  /**
   * [amended OTHERS-EXPAND] A section `pnpm expand:others` created out of an `Others` unit —
   * `MTO SEC`, `SPS(SB)`. Its own label rather than `OTHER`'s type fallback, so the tree can
   * tell a section from the `Others` pen it was carved out of.
   */
  SECTION: 'Section',
  OTHER: 'Other',
};

/** The sub-type when it says something, the type when it does not. */
export const orgUnitKindLabel = (type: OrgUnitType, subType: OrgUnitSubType): string =>
  subType === 'OTHER' ? ORG_UNIT_TYPE_LABEL[type] : ORG_UNIT_SUBTYPE_LABEL[subType];

/**
 * The role, with NO node. ⚠️ [amended C4a §8] A PERSON's role is `accessRoleLabel` below; this stays
 * for a role as a CATEGORY (a filter, a layout's role picker) and as that function's fallback.
 */
export const ACCESS_ROLE_LABEL: Record<AccessRole, string> = {
  SUPER_ADMIN: 'Super admin',
  NODAL_ADMIN: 'Nodal admin',
  NODAL_READ: 'Nodal (read)',
  NODAL_MONITOR: 'Nodal (monitor)',
  NONE: 'Mobile only',
};

const NODAL_LEVEL_WORD: Record<NodalLevel, string> = { HQ: 'HQ', DIVISION: 'Division', PS: 'PS' };

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §10 · amended P7A §2] THE RAISER PILL'S WORDS — `HQ` · `DRO` · `Unit`, one per
 *  `RAISER_LEVELS` member, exhaustive, so a new level is a compile error rather than a blank pill.
 *  (It replaces [C5c §10]'s `RAISED_AT_HQ`, which had one word because the field was a boolean.)
 *
 *  `HQ` is `NODAL_LEVEL_WORD.HQ`, so a nodal reads one "HQ" on the officers list ("HQ Admin") and on
 *  the arrangement rows, never two spellings of it.
 *
 *  ⚠️ `DRO` IS NOT `NODAL_LEVEL_WORD.DIVISION` ("Division"), AND THAT IS THE CLIENT'S WORD. It is
 *  also the name of a division's reserve-office UNIT on this very screen (`DRO-CEN`), which is why
 *  the `title` spells the level out: "Raised at division level". Duty Mitra's `raiser.*` catalogue
 *  says the same words, and `statusWords.test.ts` holds the two apps to them.
 *
 *  ⚠️ "HQ" ANSWERS TWO PREDICATES, AND THEY ARE NOT THE SAME ONE. `accessRoleLabel` names a PERSON by
 *  their node's `type` (the display label, harmless to relabel: it grants nothing); the pill names an
 *  ORDER by its raiser's POSITION (`raisedAtLevel`, derived on the server through `raiserLevelOf`),
 *  because there a relabel WOULD mislead — it would put "HQ" on a division's orders in every other
 *  division's list. So a divisional nodal who relabels their own node COMMISSIONERATE reads
 *  "HQ Admin" in their profile and raises orders reading `Unit`. Recorded, not fixed.
 *
 *  `title` is the hover text on a native `title` attribute — never a Tooltip, so the overlay census
 *  does not move ([C2b §8e]'s precedent).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const RAISER_LEVEL_LABEL: Record<RaiserLevel, { word: string; title: string }> = {
  HQ: { word: NODAL_LEVEL_WORD.HQ, title: 'Raised at commissionerate level' },
  DIVISION: { word: 'DRO', title: 'Raised at division level' },
  UNIT: { word: 'Unit', title: 'Raised at unit level' },
};
const NODAL_TIER_WORD: Record<LevelledAccessRole, string> = {
  NODAL_ADMIN: 'Admin',
  NODAL_MONITOR: 'Monitor',
  NODAL_READ: 'Read',
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C4a §8] A PERSON'S ROLE, NAMED FOR THE NODE THEY SIT AT — `HQ Admin`, `Division Monitor`,
 *  `PS Read`. Every web site that names a person's role calls this; `nodalLevelOf` in
 *  `@pams/shared` is the one derivation, and Duty Mitra reads it too, so a nodal cannot read
 *  `HQ Admin` on the officers list and `Nodal admin` in their own profile.
 *
 *  ⚠️ THE CLIENT'S WORDS VERBATIM, WHICH IS WHY THE CASE DIFFERS FROM THE FALLBACK. `Super admin`
 *  and `Mobile only` are unchanged, and a nodal with no org unit keeps `Nodal admin`.
 *
 *  ⚠️ UI ONLY. Nothing here reaches an identifier: the enum values, capabilities, routes and query
 *  keys are untouched, and `ACCESS_ROLE_LABEL` is still what a role CATEGORY reads.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const accessRoleLabel = (role: AccessRole, orgUnitType: OrgUnitType | null | undefined): string => {
  const levelled = nodalLevelOf(role, orgUnitType);
  return levelled === null
    ? ACCESS_ROLE_LABEL[role]
    : `${NODAL_LEVEL_WORD[levelled.level]} ${NODAL_TIER_WORD[levelled.role]}`;
};

/**
 * [P2] Policy version status.
 *
 * `DRAFT` is `info`, not `warning`. The four hues carry meaning (DESIGN_LANGUAGE §2) and
 * a draft is work in progress, not a problem — colouring it amber would say something
 * about it that is not true, and would spend the one colour that means "look at this".
 */
export const POLICY_VERSION_STATUS_LABEL: Record<PolicyVersionStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
};

export const POLICY_VERSION_STATUS_TONE: Record<PolicyVersionStatus, StatusTone> = {
  DRAFT: 'info',
  PUBLISHED: 'success',
};

/**
 * [P3a.1] Arrangement and duty status.
 *
 * ⚠️ PROMOTED OUT OF `pages/Arrangements/index.tsx` when the detail view became a second
 * reader. A list and a detail page disagreeing about what `ACTIVE` is called is precisely
 * the drift this file exists to prevent — and it is the kind that survives review, because
 * each screen reads correctly on its own.
 */
/**
 * ⚠️ [amended C5 §9a] TWO WORDS MOVED, AND THE SEQUENCE IS THE POINT OF BOTH.
 *
 * `Being filled` -> `Partially Filled` and `On duty` -> `Active`, so what an operator reads
 * across an order's life is `Draft · Published · Partially Filled · Active · Closed`.
 *
 * ⚠️ AND THE WORDS ARE STILL PER-CLIENT, WHICH IS A DECISION AND NOT AN OVERSIGHT.
 * `officerDutyState.constant.ts` holds the DERIVATION and no words at all — its own header
 * says so, for this file's rule (a label must never be able to alter a contract value) and for
 * the PWA's (`check:i18n` makes an imported English string untranslatable, and Bengali is
 * PA_REQUIREMENTS §7). What keeps the two catalogues honest is `statusWords.test.ts`, which
 * pins them against each other on every state they share — because `On duty` used to appear in
 * both with nothing in code tying them together.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended C5b §3] `Partially Filled` IS GONE, AND THE SEQUENCE IS FOUR WORDS: `Draft ·
 *  Published · Active · Closed`. THIS REVERSES C5 §9a FOR ONE STATUS, WITH THE CLIENT.
 *
 *  `ALLOCATION_IN_PROGRESS` is written when the FIRST officer is named and never moves again —
 *  not at 19 of 19, not back at 0 of 19. Labelled with a fill word, it contradicted the fill state
 *  beside it on every order that was not exactly half done: a client read `Partially Filled`
 *  beside `Filled: 19 of 19 · All Filled`. It reads `Published` now (decision 1), and a fill word
 *  comes from the derived fill state and nothing else.
 *
 *  ⚠️ SO THE MAP IS KEYED BY THE WORD (`arrangementStatusWordOf`), NOT THE STATUS. Re-keying is
 *  what made every call site a compile error, and a screen asks `arrangementStatusLabel(status)`
 *  or — better — `orderStatusWord` in `lib/statusPresentation.ts`, which also knows about the
 *  archive axis. `statusVocabulary.test.ts` fails on a screen indexing this map itself.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_STATUS_LABEL: Record<ArrangementStatusWord, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ACTIVE: 'Active',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const ARRANGEMENT_STATUS_TONE: Record<ArrangementStatusWord, StatusTone> = {
  DRAFT: 'neutral',
  PUBLISHED: 'info',
  ACTIVE: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
};

export const arrangementStatusLabel = (status: ArrangementStatus): string =>
  ARRANGEMENT_STATUS_LABEL[arrangementStatusWordOf(status)];

export const arrangementStatusTone = (status: ArrangementStatus): StatusTone =>
  ARRANGEMENT_STATUS_TONE[arrangementStatusWordOf(status)];

/**
 * ⚠️ [C5b §3] ONE WORD FOR AN ARCHIVED ORDER THAT WAS NOT CANCELLED.
 *
 * The record said `Archived`, the preview and the old detail page said `In the Archive`, and the
 * list said nothing at all. `orderStatusWord` is the one reader; a cancellation keeps its own word
 * because withdrawing an order that had not happened is the one thing an operator must not
 * mistake for filing.
 */
export const ARRANGEMENT_ARCHIVED_LABEL = 'Archived';

/**
 * One duty.
 *
 * ⚠️ [amended C5b §3] `PENDING` AND `ALLOCATED` BOTH READ `Published`, AND BOTH ARE `info`.
 * `ALLOCATED` is written on the FIRST name (`markWorkBegun`), so it read `Filled` over a duty with
 * one of twelve named, beside a Fill column saying `Partly named`. `Not yet filled` was the same
 * mistake from the other side. A lifecycle word may not be a fill word; the duty table's Fill
 * column is where a fill word lives.
 */
export const DEPLOYMENT_STATUS_LABEL: Record<DeploymentStatus, string> = {
  PENDING: 'Published',
  ALLOCATED: 'Published',
  /* ⚠️ [amended C5 §9a] `On duty` -> `Active`, with the arrangement's own label above. */
  ACTIVE: 'Active',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const DEPLOYMENT_STATUS_TONE: Record<DeploymentStatus, StatusTone> = {
  PENDING: 'info',
  ALLOCATED: 'info',
  ACTIVE: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'danger',
};

/**
 * [P3b.1] A standing order runs or it has ended. ⚠️ `ENDED` is `neutral`, not `danger`:
 * ending is a deliberate, ordinary act by the CP team, not a fault — the same call
 * `labels.ts` already makes about a draft being `info` rather than `warning`.
 */
export const STANDING_ORDER_STATUS_LABEL = { ACTIVE: 'Running', ENDED: 'Ended' } as const;
export const STANDING_ORDER_STATUS_TONE = { ACTIVE: 'success', ENDED: 'neutral' } as const satisfies Record<
  keyof typeof STANDING_ORDER_STATUS_LABEL,
  StatusTone
>;

/**
 * ⚠️ AN OVERLAP IS `info`, NOT `warning`. A deliberate handover overlap is a fact worth
 * knowing, not a problem; spending the amber on it is how the amber stops meaning "look at
 * this". A GAP — nobody on duty — is what the amber is for.
 */
export const COVERAGE_TONE = {
  ROUND_THE_CLOCK: 'success',
  GAPS: 'warning',
  OVERLAPS: 'info',
  NONE: 'neutral',
} as const satisfies Record<string, StatusTone>;

/**
 * [P4] Fulfilment. FR-5.3.1 fixes these four system-wide.
 *
 * ⚠️ [amended C5b §3] KEYED BY THE FILL WORD — the four states plus `CLOSED_SHORT` — so COMPLETE's
 * two causes are two keys rather than a map and a special case beside it. `fillWordOf`
 * (`@pams/shared`) picks the key; a screen calls `fillBadge` in `lib/statusPresentation.ts`.
 */
export const FILL_WORD_LABEL: Record<FillWord, string> = {
  NOT_NAMED: 'Nobody named',
  PARTIAL: 'Partly named',
  NAMED: 'All named',
  /* ⚠️ [amended C5 §9a] `Complete` -> `All Filled`. [C5b §3]: a COMPLETE that is not closed short
     has `named >= allocated > 0` by construction (`fillOfTotals`), so this word always has its names. */
  COMPLETE: 'All Filled',
  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5 §3b] A COMPLETION HAS TWO WORDS, BECAUSE IT HAS TWO CAUSES.
   *
   *  A client reported a live order reading `Complete` with NOBODY named: every division had
   *  closed its share at `0 of 12` under [C3c.2]'s Complete With Short-Fall, which IS complete
   *  by decision. §3b: "the label is the defect, not the state" — "Complete" for an order nobody
   *  is coming to is the single worst thing this product can say.
   *
   *  ⚠️ A WORD, NEVER A FIFTH `FILL_STATES` MEMBER, and `fillState.constant.ts` states why: a
   *  member would move `allocation_fills.fill_rank`'s stored `{min: 0, max: 3}` and re-open every
   *  query that reads it. The second axis rides on the DTO as `closedShort`.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  CLOSED_SHORT: 'Closed short',
};

export const fillStateLabel = (state: FillState, closedShort: boolean): string =>
  FILL_WORD_LABEL[fillWordOf(state, closedShort)];

/**
 * ⚠️ THE ONE PLACE IN THIS FILE WHERE AN UNSTARTED STATE IS `danger` RATHER THAN `neutral`,
 * AND THE REASON HAS TO BE WRITTEN DOWN OR SOMEBODY WILL "HARMONISE" IT.
 *
 * This file says twice that DRAFT and PENDING are neutral because waiting is not a problem,
 * and that is still true of both: a draft is the author's own unfinished work, and a PENDING
 * duty is one nobody has been asked about yet. An allocation with nobody named is NEITHER —
 * it is a request from another office that has not been answered, and FR-5.3.1 fixes the
 * colour system-wide: red = not mapped, amber = partial, green = mapped, green + a lock =
 * marked complete. `DEPLOYMENT_STATUS_TONE.PENDING` stays neutral.
 *
 * ⚠️ [amended C5b §3] `DEPLOYMENT_STATUS_TONE.PENDING` IS `info` NOW — it reads `Published`, and a
 * published order is `info`. The argument above is unchanged: waiting is still not red.
 *
 * ⚠️ AND THE CLOSED-SHORT WORD KEEPS `success`, AS C5 DECIDED. A declared short close IS
 * discharged work — the division did what it could and said so. The WORD is what stops it
 * claiming the duty is staffed; a fifth hue would be a new meaning in the four-colour system.
 */
export const FILL_WORD_TONE: Record<FillWord, StatusTone> = {
  NOT_NAMED: 'danger',
  PARTIAL: 'warning',
  NAMED: 'success',
  COMPLETE: 'success',
  CLOSED_SHORT: 'success',
};

/**
 * ⚠️ [C5b §3] THE TONE OF A FILL FACT — AND A ZERO ASK IS `neutral`, NEVER RED.
 *
 * `NOT_NAMED` is also what `fillStateOf` answers for a requirement of zero, and the map above
 * painted it red: the colour of an unanswered request, on a request nobody made. The dashboard's
 * red count already guarded it out; the web did not. `isUnanswered` (`@pams/shared`) is the one
 * red — nobody named AND something asked — and Duty Mitra's pill calls the same predicate.
 */
export const fillTone = (facts: { fillState: FillState; closedShort: boolean; allocatedCount: number }): StatusTone =>
  facts.fillState === 'NOT_NAMED' && !isUnanswered(facts)
    ? 'neutral'
    : FILL_WORD_TONE[fillWordOf(facts.fillState, facts.closedShort)];

/**
 * [C5b §3] The red-count card's two zero sentences. `unnamedZero` lived in the dashboard's copy
 * until the zero ask gave it a sibling; `linesUnnamedFigure` is their one reader.
 */
export const LINES_UNNAMED_ZERO_LABEL = {
  /** Something was asked and every slot is named — a finished job. */
  allNamed: 'Every line is named',
  /** Nothing was asked — not an achievement, and not a problem (D15). */
  noAsk: 'No force asked for',
} as const;

/**
 * ⚠️ [C5c §5a] THE THREE ATTENDANCE WORDS — `presenceOf`'s states, worded. Words only: the rule that
 * picks one is `presenceWord` / `presenceLine` in `lib/statusPresentation.ts`, and
 * `statusVocabulary.test.ts` fails on a screen that indexes this map. `Present`, never `Reached`,
 * on every surface (U4) — `statusWords.test.ts` holds these to Duty Mitra's `presence.*` keys.
 */
export const PRESENCE_LABEL: Record<Presence, string> = {
  NOT_ARRIVED: 'Not arrived',
  PRESENT: 'Present',
  ABSENT: 'Absent',
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIFECYCLE] "Archived · was published" — an archived row says what it WAS.
 *
 *  ⚠️ IT COMPOSES WITH THE PRESERVED STATUS RATHER THAN REPLACING IT. Archive is an AXIS,
 *  not a status: an order that was published and then withdrawn is a different thing from
 *  a draft nobody ever issued, and a row that renders a bare "Archived" has thrown away the
 *  only fact that distinguishes them — which is also the fact that decides whether it can
 *  ever be purged.
 *
 *  ⚠️ WHERE THERE WAS NEVER A SECOND STATE IT SAYS SO BY SAYING LESS. A post type and a
 *  vehicle type were a boolean; composing "was active" for them would be an invention, so
 *  they get the bare word and nothing else.
 *
 *  ⚠️ AND IT IS ONE STRING, FOR ONE `StatusDot`. Two elements would be two `getByText`
 *  targets for one fact, and would read aloud as two separate statuses.
 */
export const archivedLabel = (preservedStatusLabel?: string | null): string =>
  preservedStatusLabel ? `Archived · was ${preservedStatusLabel.toLowerCase()}` : 'Archived';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P6a] THE TILE CATALOGUE'S WORDS.
 *
 *  ⚠️ HERE RATHER THAN IN THE REGISTRY, AND IT IS A DELIBERATE DEPARTURE FROM THE BRIEF'S
 *  FIELD LIST. `packages/shared/src/constants/dashboardTile.constant.ts` declares what a tile
 *  IS; a `name` and a `description` in it would be untranslatable by construction, because
 *  P6d renders the same registry in an app where every user-facing string goes through `t()`
 *  and `check:i18n` is a CI gate. This file already states the rule the split follows: the
 *  API's enum values are the contract, and a label change must never be able to alter one.
 *
 *  ⚠️ EXHAUSTIVE, so a tile added upstream WITHOUT WORDS is a compile error rather than a
 *  blank card — `ORG_UNIT_SUBTYPE_LABEL`'s guarantee, and the same one `i18n.test.ts` gives
 *  the officer app.
 *
 *  ⚠️ AND THE `hint` IS WHERE A NUMBER'S BOUNDARY IS STATED. Two of these say something an
 *  operator would otherwise get wrong: a fairness leaderboard that cannot see another
 *  division's orders, and a lateness figure computed from a handset clock.
 *
 *  ⚠️ [amended C1e §3] THE `hint` IS OVERRIDABLE NOW, AND THIS BLOCK USED TO SAY THE OPPOSITE.
 *
 *  [P6b] made it structural and held that with a SHAPE — "`lib/tileWords.ts` has no argument
 *  through which a hint could be supplied", "`dashboardLayout.schema.ts` has no key for one".
 *  Both now do, on a client decision, as a TRI-STATE: `null` is this sentence, `''` HIDES the
 *  line entirely, and text replaces it.
 *
 *  ⚠️ WHAT THIS FILE STILL IS, AND WHY IT MATTERS MORE RATHER THAN LESS: the sentence below is
 *  where a number's BOUNDARY is stated, and the configurator and the tile library render it
 *  from HERE — `DASHBOARD_TILE_LABEL` directly, never through `resolveTileWords` — so an
 *  operator who hides it on the card still reads it, at rest, beside the field they are
 *  editing. That asymmetry is the whole of what bounds the reversal, and
 *  `packages/shared/src/schema/dashboardLayout.schema.ts` has no key for one.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * [P6b.1] Where a shortcut may point, in the operator's words.
 *
 * ⚠️ EXHAUSTIVE, so a destination added to the catalogue without words is a compile error
 * rather than a blank option in a picker — `ORG_UNIT_SUBTYPE_LABEL`'s guarantee.
 *
 * ⚠️ AND THEY ARE THE SIDEBAR'S OWN WORDS. A shortcut to the inbox reading anything other
 * than "Assign PA" would be a second vocabulary for one screen, which is the defect [D4.5]
 * recorded when the deleted Home page's card said "Open the inbox" under a heading that had
 * been renamed. These are the DEFAULTS an operator sees before they type their own title.
 */
export const DASHBOARD_SHORTCUT_LABEL: Record<DashboardShortcutTarget, string> = {
  INBOX: 'Assign PA',
  ARRANGEMENTS: 'Manage Arrangements',
  ARRANGEMENT_NEW: 'New arrangement',
  STANDING_ORDERS: 'Round the clock',
  ORG_TREE: 'Organisation',
  OFFICERS: 'Officers',
  PLACES: 'Places',
  POLICIES: 'Policies',
  POST_TYPES: 'Post types',
  VEHICLE_TYPES: 'Vehicle types',
};

/**
 * [P6b] How wide a tile sits on the grid, for the configurator's control.
 *
 * ⚠️ NO PIXEL AND NO COLUMN COUNT IN A LABEL. `TILE_SIZES`' own header forbids it: a size is a
 * statement about the CONTENT, and the span arithmetic is presentation that lives in
 * `lib/dashboardGrid.ts`. The screen says how many columns the grid has once, in prose,
 * beneath the list — a number in a label would go stale the day the ladder changes.
 */
export const TILE_SIZE_LABEL: Record<TileSize, string> = {
  sm: 'Small',
  md: 'Medium',
  lg: 'Wide',
};

export const DASHBOARD_TILE_LABEL: Record<DashboardTileId, { title: string; hint: string; drill: string }> = {
  ORDERS_BY_STATUS: {
    title: 'Arrangements in range',
    hint: 'Orders with duty inside these dates, by where each one has got to.',
    drill: 'Open the arrangement list',
  },
  LINES_UNNAMED: {
    title: 'Lines still unnamed',
    hint: 'Force slots asked of your units that nobody has been named into yet.',
    drill: 'Open the inbox',
  },
  REQUIREMENT_FULFILMENT: {
    title: 'Requirement fulfilment',
    /*
     * ⚠️ [amended C2b §6a] IT NAMES THE ROW, NOT THE TOTALS — the card is a list now.
     *
     * ⚠️ THE FIXED WINDOW IS NOT CLAIMED HERE. [C1e §3] made the hint operator-overridable, so
     * a structural fact stated only in it can be edited away by the person it protects. The
     * window is on the PAYLOAD (`windowLabel`) and the card renders it.
     */
    hint: 'One row per order, most short of names first. Within your scope only.',
    drill: 'Open the inbox',
  },
  DUTIES_UNSTAFFED: {
    title: 'Duties with nobody named',
    hint: 'A place and a night that has been asked for and has no one against it at all.',
    drill: 'Open the inbox',
  },
  ATTENDANCE: {
    title: 'Attendance',
    hint: 'Officers on force duty in range. Command roles are exempt and are not counted.',
    drill: 'See the breakdown',
  },
  SWAPS: {
    title: 'Swaps',
    hint: 'Officers replaced after the order was issued.',
    drill: 'See which orders',
  },
  LOAD_BY_DIVISION: {
    title: 'Load by division',
    hint: 'How much force each division was asked for, and how much it has named.',
    drill: 'See every division',
  },
  LOAD_BY_UNIT: {
    title: 'Load by unit',
    hint: 'The same, for the units the orders address directly.',
    drill: 'See every unit',
  },
  REPEAT_DEPLOYMENT: {
    title: 'Deployed most often',
    hint: 'Within your scope only — an officer another division also uses will read low here.',
    drill: 'See the full list',
  },
  PUBLISH_TO_COMPLETE: {
    title: 'Publish to complete',
    hint: 'Median hours from an order being issued to a division marking its share complete.',
    drill: 'See every division',
  },
  LATE_START: {
    title: 'Late start of duty',
    hint: 'From the officer’s own handset clock. Implausible clocks are left out and counted.',
    drill: 'See the breakdown',
  },
  VEHICLE_FULFILMENT: {
    title: 'Vehicles',
    /* ⚠️ [P7A §3] What is REQUIRED, and nothing beside it — a vehicle is never shown short. */
    hint: 'What the orders in range require, by vehicle type.',
    drill: 'See every type',
  },
  ORDERS_BY_POLICY: {
    title: 'Arrangements by policy',
    hint: 'Which policy the orders in range were raised against.',
    drill: 'See every policy',
  },
  /*
   * ⚠️ [P6b.1] A SHORTCUT'S WORDS ARE A PLACEHOLDER, NOT A DESCRIPTION.
   *
   * Every other entry here describes what a NUMBER counts, and its `hint` is where that
   * number's boundary is stated — which is why a hint is structural and cannot be overridden.
   * A shortcut has no number, so it has no boundary to state and nothing to be honest about:
   * these are simply what the card says until an operator types their own. That is why §3c's
   * rule does not bite here, and it is the cleanest possible statement of what that rule was
   * ever about.
   *
   * ⚠️ [amended P6b.2] ONE ENTRY FOR EVERY PLACEMENT, and that is the point: a placement's
   * own title is what distinguishes two shortcuts, so the catalogue word is only ever the
   * placeholder an unconfigured one shows.
   */
  SHORTCUT: { title: 'Shortcut', hint: 'A link to another screen.', drill: 'Open' },
  /**
   * ⚠️ [C1e §1a] THE ONLY TILE WHOSE HINT STATES A GRAIN RATHER THAN A BOUNDARY, and it needs
   * to: a row is one DUTY, so a three-card order appears three times and a reader counting
   * rows would otherwise count orders.
   */
  ARRANGEMENTS_TODAY: {
    title: 'On today',
    hint: 'One row per duty point in range, within your scope only.',
    drill: 'See every duty',
  },
  /**
   * ⚠️ [C1g §1e] THE HINT SAYS WHICH DATES, AND IT HAS TO.
   *
   * This tile obeys the From/To picker like every other one (FR-5.5.2); the notification bell
   * in the utility bar uses the inbox's own window instead, because it has no picker. Until an
   * operator moves the picker the two agree exactly — and after that the reader needs to know
   * which of the two numbers on their screen is the one that moved. A caveat is where a
   * number's BOUNDARY is stated, which is what a hint is for.
   */
  ORDERS_TO_FILL: {
    title: 'Still to fill',
    hint: 'Orders in the selected dates with names still to add, within your scope only.',
    drill: 'Go to Assign PA',
  },
  /**
   * ⚠️ [ASSOC-TILE] THE HINT NAMES THE FIELD, BECAUSE THE FIELD IS THE WHOLE ANSWER TO "WHY IS
   * THIS ORDER ON MY SCREEN?".
   *
   * A station is granted sight of an order three ways ([C1b §1]) and only one of them is this
   * list. Without the field's own name — "Associated PS / Unit", exactly as it is labelled in
   * the PA intake — a reader cannot tell this card from the arrangement list, which shows
   * everything their subtree can see. That is where a number's BOUNDARY is stated, which is
   * what a hint is for.
   *
   * ⚠️ AND IT SAYS "READ ONLY" BECAUSE THE ROWS GENUINELY REACH NOTHING ELSE. An association
   * carries no share and no obligation, so the honest promise of the card is that it opens the
   * order and nothing more.
   */
  ASSOCIATED_ORDERS: {
    title: 'Attached to us',
    hint: 'Orders in the selected dates that name your own unit under Associated PS / Unit. Read only.',
    drill: 'See every order',
  },
  DIVISION_ROLLUP: {
    title: 'By division',
    hint: 'What each division was asked for, and how much of it is named. Their own share only.',
    drill: 'See every division',
  },
  STANDING_ORDER_COVERAGE: {
    title: 'Round-the-clock coverage',
    hint: 'Nights the generator was due to raise, against the nights it actually did.',
    drill: 'See every standing order',
  },
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B §5] A TYPED ORDER'S WORDS.
 *
 *  The type's own label is the registry's (`ARRANGEMENT_TYPES[key].label` — `Durga Puja PA`), read
 *  through this one function so a stray or retired key renders nothing rather than a raw code.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const arrangementTypeLabel = (type: string | null | undefined): string | null =>
  isArrangementType(type) ? ARRANGEMENT_TYPES[type].label : null;

/** A typed DRAFT held back from publish because a by-name in-charge on it names nobody ([P7B] decision 2). */
export const IN_CHARGE_UNNAMED_LABEL = 'In-charge not named';

/**
 * "From t12 · 2026 booklet · part 2 of 6" — a single-part order says no part, because "part 1 of 1" is noise.
 *
 * ⚠️ [amended DP2026] THE BOOKLET'S YEAR IS NAMED, the user's choice. Two booklets of one event make orders of
 * the same type and both number their templates from t1, so "From t12" alone stopped saying which template.
 * The year is the set key's `-YYYY` suffix — `import:templates` refuses any other key — and a key without
 * one reads as before.
 */
export const templateSourceLabel = (source: {
  setKey: string;
  templateKey: string;
  part: number;
  parts: number;
}): string => {
  const year = /-(\d{4})$/.exec(source.setKey)?.[1];
  const from = year === undefined ? `From ${source.templateKey}` : `From ${source.templateKey} · ${year} booklet`;
  return source.parts > 1 ? `${from} · part ${String(source.part)} of ${String(source.parts)}` : from;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3/§4] THE TEMPLATE PAGES' VOCABULARIES — each a `Record` over the shared tuple, so a class,
 *  a readiness state or a run outcome added upstream without words is a compile error rather than a
 *  raw enum member on a screen ([D4.5]'s divergence #84).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ⚠️ [amended P7B.1 §3d] WHAT A TEMPLATE IS ON THE LIBRARY'S SCREEN: its readiness — or ALREADY CREATED,
 * which is not a readiness state at all but what a person needs to see first. These words REPLACE the
 * readiness maps, and `statusPresentation.ts`'s `templateStateWord` is the one rule that picks one
 * (`statusVocabulary.test.ts` refuses a screen that indexes these itself).
 *
 * ⚠️ NO NEW COLOUR MEANING ([P7B.1], the user's decision): ready is green and blocked amber, as before;
 * already created is grey — the template flow's own ALREADY_DONE tone — and so is "not built", told
 * apart from it by its words.
 */
export const TEMPLATE_STATES = ['CREATED', 'READY', 'BLOCKED', 'ROUND_THE_CLOCK', 'EMPTY'] as const;
export type TemplateState = (typeof TEMPLATE_STATES)[number];

export const TEMPLATE_STATE_LABEL: Record<TemplateState, string> = {
  CREATED: 'Already created',
  READY: 'Ready',
  BLOCKED: 'Blocked',
  ROUND_THE_CLOCK: 'Round the clock — not built',
  EMPTY: 'No duty points',
};

export const TEMPLATE_STATE_TONE: Record<TemplateState, StatusTone> = {
  CREATED: 'neutral',
  READY: 'success',
  BLOCKED: 'warning',
  ROUND_THE_CLOCK: 'neutral',
  EMPTY: 'neutral',
};

export const TEMPLATE_DECISION_CLASS_LABEL: Record<TemplateDecisionClass, string> = {
  SOURCE: 'Sources and units',
  FORCE_COLUMN: 'Force columns',
  POLICY: 'Policies',
  IN_CHARGE_ROLE: 'In-charge roles',
  TOTALS: 'Printed totals',
  SCHEDULE: 'Days and times',
  DUTY_POINT: 'Duty points',
};

/** Each class's one plain question — the decisions page states it above the rows. */
export const TEMPLATE_DECISION_CLASS_QUESTION: Record<TemplateDecisionClass, string> = {
  SOURCE: 'Which unit provides the force the booklet asks of this source — or leave those rows out.',
  FORCE_COLUMN: 'Which post type or vehicle type this booklet column is — a one-off column, or leave it out.',
  POLICY: 'Which policy carries this set of columns. A duty point binds the policy that covers its columns.',
  IN_CHARGE_ROLE: 'Which role on the policy this booklet in-charge is — a one-off role, or drop it.',
  /** [TEMPLATE-FIND] What a printed total is, and what to check — said where the choice is made. */
  TOTALS:
    'Under each table the booklet prints a Total row. PAMS adds up the rows it read from that table, and here the two differ. Open the booklet at that page and add the column up yourself: if the rows come to what PAMS read, the booklet’s own total is wrong — use the rows as read. If a row was read wrongly, correct it so the column comes to the printed total. A printed Inspector or AC total can include the officers in the in-charge cells. If you cannot tell, hold the template back.',
  SCHEDULE: 'Which festival days this section runs on, and at what times.',
  DUTY_POINT:
    'This duty point cannot be built as read. Type its place, or leave it out. Booklet text PAMS could not read is shown under it, and often holds the place.',
};

export const TEMPLATE_RUN_OUTCOME_LABEL: Record<TemplateRunOutcome, string> = {
  PENDING: 'Waiting',
  CREATED: 'Created',
  PUBLISHED: 'Published',
  ALREADY_DONE: 'Already done',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

export const TEMPLATE_RUN_OUTCOME_TONE: Record<TemplateRunOutcome, StatusTone> = {
  PENDING: 'neutral',
  CREATED: 'success',
  PUBLISHED: 'success',
  ALREADY_DONE: 'neutral',
  FAILED: 'danger',
  SKIPPED: 'warning',
};
